// src/Components/Broadcast/components/GiftBroadcastPanel.js
import React, { useState, useEffect } from 'react';
import { 
  Gift, Clock, CheckCircle, RefreshCw, 
  BarChart, Users, Mail, Calendar, ChevronDown, 
  Trash2, Plus, Edit, Save, X, 
  Play, Pause, AlertCircle,
  MessageSquare, Image, Database, TrendingUp, 
  Bell, Settings as SettingsIcon,
  FileText, Code, Eye as EyeIcon, Loader, Power,
  Upload, List, Timer, User, Image as ImageIcon, UserX, Trash, CheckSquare, Square
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://artdownload2-production.up.railway.app';

export const GiftBroadcastPanel = ({ showGiftPanel, onClose }) => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [systemSettings, setSystemSettings] = useState({ enabled: true });
  const [loading, setLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [removingItem, setRemovingItem] = useState(null);
  
  // Состояния для множественного выбора
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  // Форма для новой кампании
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    isActive: true,
    giftTypes: [],
    sendTo: 'all',
    messageSequence: [
      { type: 'first_gift', day: 1, delay: 1, subject: '🎁 Спасибо за подарок!', enabled: true, hasImage: false, imageUrl: null },
      { type: 'gift_feedback', day: 2, delay: 24, subject: '✨ Как вам подарок?', enabled: true, hasImage: false, imageUrl: null },
      { type: 'special_offer', day: 3, delay: 48, subject: '🔥 Специальное предложение для вас', enabled: true, hasImage: false, imageUrl: null },
      { type: 'last_chance', day: 5, delay: 96, subject: '🎉 Не упустите возможность!', enabled: true, hasImage: false, imageUrl: null }
    ],
    startDate: null,
    endDate: null,
    createdAt: new Date().toISOString()
  });

  // Загрузка настроек системы
  const loadSystemSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift-broadcast/settings`);
      if (response.data.success) {
        setSystemSettings({ enabled: response.data.enabled });
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
    }
  };

  // Загрузка ВСЕХ запланированных сообщений
  const loadScheduledMessages = async () => {
    try {
      setScheduledLoading(true);
      const response = await axios.get(`${API_URL}/api/gift-broadcast/all-scheduled`);
      if (response.data.success) {
        setScheduledMessages(response.data.items || []);
        console.log(`📋 Loaded ${response.data.items?.length || 0} scheduled messages`);
      } else {
        console.error('Error loading scheduled messages:', response.data.error);
      }
    } catch (error) {
      console.error('Error loading scheduled messages:', error);
    } finally {
      setScheduledLoading(false);
    }
  };

  // Выбор/снятие выбора пользователя
  const toggleSelectUser = (userId) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  // Выбрать всех пользователей
  const selectAllUsers = () => {
    const uniqueUserIds = [...new Set(scheduledMessages.map(msg => msg.userId))];
    setSelectedUserIds(new Set(uniqueUserIds));
  };

  // Снять все выделения
  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  // Массовое удаление выбранных пользователей
  const bulkRemoveUsers = async () => {
    if (selectedUserIds.size === 0) {
      showNotification('warning', 'Не выбрано ни одного пользователя');
      return;
    }
    
    const userCount = selectedUserIds.size;
    if (!window.confirm(`Удалить ${userCount} пользователей из очереди? Все их сообщения будут отмечены как отправленные.`)) {
      return;
    }
    
    setScheduledLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const userId of selectedUserIds) {
      const userName = scheduledMessages.find(m => m.userId === userId)?.userName || `User ${userId}`;
      try {
        const response = await axios.delete(`${API_URL}/api/gift-broadcast/queue/user/${userId}`, {
          data: { reason: 'bulk_removal_from_panel' }
        });
        
        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to remove user ${userId}:`, response.data.error);
        }
      } catch (error) {
        failCount++;
        console.error(`Error removing user ${userId}:`, error);
      }
    }
    
    showNotification('success', `Удалено ${successCount} пользователей из очереди${failCount > 0 ? `, ${failCount} ошибок` : ''}`);
    
    // Сбрасываем выделение и обновляем список
    setSelectedUserIds(new Set());
    setBulkDeleteMode(false);
    await loadScheduledMessages();
    setScheduledLoading(false);
  };

  // Удаление пользователя из очереди (одиночное)
  const removeUserFromQueue = async (userId, userName) => {
    if (!window.confirm(`Удалить пользователя "${userName}" (ID: ${userId}) из очереди? Все его сообщения будут отмечены как отправленные.`)) {
      return;
    }
    
    setRemovingItem(userId);
    try {
      const response = await axios.delete(`${API_URL}/api/gift-broadcast/queue/user/${userId}`, {
        data: { reason: 'manual_removal_from_panel' }
      });
      
      if (response.data.success) {
        showNotification('success', `Пользователь ${userName} удален из очереди. Удалено ${response.data.removedCount} сообщений.`);
        loadScheduledMessages();
      } else {
        showNotification('error', response.data.error || 'Ошибка при удалении пользователя');
      }
    } catch (error) {
      console.error('Error removing user from queue:', error);
      showNotification('error', 'Ошибка при удалении пользователя');
    } finally {
      setRemovingItem(null);
    }
  };

  // Удаление конкретного сообщения из очереди
  const removeMessageFromQueue = async (broadcastId, step, userId, userName) => {
    if (!window.confirm(`Удалить сообщение для пользователя "${userName}" (шаг ${step + 1}) из очереди?`)) {
      return;
    }
    
    setRemovingItem(`${broadcastId}_${step}`);
    try {
      const response = await axios.delete(`${API_URL}/api/gift-broadcast/queue/message/${broadcastId}/${step}`);
      
      if (response.data.success) {
        showNotification('success', `Сообщение для ${userName} удалено из очереди`);
        loadScheduledMessages();
      } else {
        showNotification('error', response.data.error || 'Ошибка при удалении сообщения');
      }
    } catch (error) {
      console.error('Error removing message from queue:', error);
      showNotification('error', 'Ошибка при удалении сообщения');
    } finally {
      setRemovingItem(null);
    }
  };

  // Включение/выключение системы
  const toggleSystem = async () => {
    try {
      const newState = !systemSettings.enabled;
      const response = await axios.post(`${API_URL}/api/gift-broadcast/settings`, { enabled: newState });
      if (response.data.success) {
        setSystemSettings({ enabled: newState });
        showNotification('success', `Система рассылок ${newState ? 'включена' : 'выключена'}`);
      }
    } catch (error) {
      console.error('Error toggling system:', error);
      showNotification('error', 'Ошибка при изменении состояния системы');
    }
  };

  // Загрузка кампаний
  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/gift-broadcast/campaigns`);
      if (response.data.success) {
        const campaignsWithFixedImages = response.data.data.map(campaign => {
          if (campaign.messageSequence) {
            campaign.messageSequence = campaign.messageSequence.map(msg => ({
              ...msg,
              hasImage: !!(msg.imageUrl && msg.imageUrl !== null && msg.imageUrl !== ''),
              imageUrlOriginal: msg.imageUrl
            }));
          }
          return campaign;
        });
        setCampaigns(campaignsWithFixedImages);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      showNotification('error', 'Ошибка загрузки кампаний');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка шаблонов
  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift-broadcast/templates`);
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      showNotification('error', 'Ошибка загрузки шаблонов');
    }
  };

  // Загрузка статистики
  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gift-broadcast/stats`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ total: 0, pending: 0, sent: 0, failed: 0, totalMessages: 0, sentMessages: 0 });
    }
  };

  // Сохранение кампании на сервер
  const saveCampaignToServer = async (campaign) => {
    try {
      await axios.post(`${API_URL}/api/gift-broadcast/campaigns`, campaign);
    } catch (error) {
      console.error('Error saving campaign to server:', error);
      throw error;
    }
  };

  // Сохранение шаблона на сервер
  const saveTemplateToServer = async (template) => {
    try {
      await axios.post(`${API_URL}/api/gift-broadcast/templates`, template);
    } catch (error) {
      console.error('Error saving template to server:', error);
      throw error;
    }
  };

  // Создание новой кампании
  const createCampaign = async () => {
    const newId = Date.now().toString();
    const campaign = {
      ...newCampaign,
      id: newId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    try {
      await saveCampaignToServer(campaign);
      setCampaigns([...campaigns, campaign]);
      setSelectedCampaign(campaign);
      setShowEditor(true);
      showNotification('success', 'Кампания создана');
    } catch (error) {
      showNotification('error', 'Ошибка при создании кампании');
    }
  };

  // Обновление кампании
  const updateCampaign = async (campaign) => {
    try {
      const updatedCampaign = { ...campaign, updatedAt: new Date().toISOString() };
      await saveCampaignToServer(updatedCampaign);
      const updated = campaigns.map(c => c.id === campaign.id ? updatedCampaign : c);
      setCampaigns(updated);
      setSelectedCampaign(updatedCampaign);
      showNotification('success', 'Кампания обновлена');
    } catch (error) {
      showNotification('error', 'Ошибка при обновлении кампании');
    }
  };

  // Удаление кампании
  const deleteCampaign = async (id) => {
    if (window.confirm('Удалить эту кампанию?')) {
      try {
        await axios.delete(`${API_URL}/api/gift-broadcast/campaigns/${id}`);
        const updated = campaigns.filter(c => c.id !== id);
        setCampaigns(updated);
        if (selectedCampaign?.id === id) {
          setSelectedCampaign(null);
          setShowEditor(false);
        }
        showNotification('success', 'Кампания удалена');
      } catch (error) {
        console.error('Error deleting campaign:', error);
        showNotification('error', 'Ошибка при удалении кампании');
      }
    }
  };

  // Активация/деактивация кампании
  const toggleCampaign = (campaign) => {
    const updated = { ...campaign, isActive: !campaign.isActive };
    updateCampaign(updated);
  };

  // Создание нового шаблона
  const createTemplate = () => {
    const newTemplate = {
      id: `template_${Date.now()}`,
      name: 'Новый шаблон',
      type: `custom_${Date.now()}`,
      icon: '📝',
      subject: 'Новый шаблон',
      text: 'Содержимое шаблона',
      variables: ['user.fullName', 'gift.title'],
      isActive: true,
      createdAt: new Date().toISOString()
    };
    setEditingTemplate(newTemplate);
    setShowTemplateEditor(true);
  };

  // Обновление шаблона
  const updateTemplate = async (template) => {
    try {
      await saveTemplateToServer(template);
      const updated = templates.map(t => t.id === template.id ? template : t);
      setTemplates(updated);
      showNotification('success', 'Шаблон сохранен');
    } catch (error) {
      showNotification('error', 'Ошибка при сохранении шаблона');
    }
  };

  // Удаление шаблона
  const deleteTemplate = async (id) => {
    if (window.confirm('Удалить этот шаблон?')) {
      try {
        await axios.delete(`${API_URL}/api/gift-broadcast/templates/${id}`);
        const updated = templates.filter(t => t.id !== id);
        setTemplates(updated);
        showNotification('success', 'Шаблон удален');
      } catch (error) {
        console.error('Error deleting template:', error);
        showNotification('error', 'Ошибка при удалении шаблона');
      }
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeRemaining = (scheduledAt) => {
    const now = new Date();
    const scheduled = new Date(scheduledAt);
    const diff = scheduled - now;
    
    if (diff <= 0) {
      return 'Ожидает отправки';
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} д. ${hours % 24} ч.`;
    }
    
    if (hours > 0) {
      return `${hours} ч. ${minutes} мин.`;
    }
    
    if (minutes > 0) {
      return `${minutes} мин. ${seconds} сек.`;
    }
    
    return `${seconds} сек.`;
  };

  // Получить уникальных пользователей для отображения количества выбранных
  const getUniqueUserCount = () => {
    return new Set(scheduledMessages.map(m => m.userId)).size;
  };

  useEffect(() => {
    if (showGiftPanel) {
      loadSystemSettings();
      loadCampaigns();
      loadTemplates();
      loadStats();
      loadScheduledMessages();
    }
  }, [showGiftPanel]);

  // Автообновление
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      loadScheduledMessages();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh]);

  if (!showGiftPanel) return null;

  const getTabClass = (tabId, color) => {
    if (activeTab === tabId) {
      if (color === 'purple') return 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg';
      if (color === 'blue') return 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg';
      if (color === 'green') return 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg';
      if (color === 'orange') return 'bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-lg';
      return 'bg-gradient-to-r from-gray-600 to-gray-700 text-white shadow-lg';
    }
    return 'text-gray-400 hover:text-white hover:bg-gray-700/50';
  };

  // Группируем сообщения по пользователям для отображения чекбоксов
  const getUserGroups = () => {
    const userMap = new Map();
    scheduledMessages.forEach(msg => {
      if (!userMap.has(msg.userId)) {
        userMap.set(msg.userId, {
          userId: msg.userId,
          userName: msg.userName,
          messages: []
        });
      }
      userMap.get(msg.userId).messages.push(msg);
    });
    return Array.from(userMap.values());
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 backdrop-blur-md">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700/50">
        
        {/* Уведомления */}
        {notification.message && (
          <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl animate-in slide-in-from-top-2 ${
            notification.type === 'success' 
              ? 'bg-gradient-to-r from-green-600 to-green-700 text-white border border-green-500' 
              : notification.type === 'error'
              ? 'bg-gradient-to-r from-red-600 to-red-700 text-white border border-red-500'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white border border-blue-500'
          }`}>
            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : 
             notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : 
             <Bell className="w-5 h-5" />}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        )}

        {/* Заголовок */}
        <div className="px-6 py-5 border-b border-gray-700/50 flex items-center justify-between bg-gray-800/30 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Автоматические рассылки подарков</h2>
              <p className="text-sm text-gray-400 mt-0.5">Управление цепочками сообщений для пользователей, получивших подарки</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSystem}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                systemSettings.enabled
                  ? 'bg-green-600/20 text-green-400 border border-green-500/50 hover:bg-green-600/30'
                  : 'bg-gray-700/50 text-gray-400 border border-gray-600 hover:bg-gray-700'
              }`}
            >
              <Power className="w-4 h-4" />
              <span className="text-sm font-medium">
                {systemSettings.enabled ? 'Система активна' : 'Система отключена'}
              </span>
            </button>
            <button 
              onClick={onClose} 
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-xl transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Вкладки */}
        <div className="flex gap-1 p-4 border-b border-gray-700/50 bg-gray-800/20">
          {[
            { id: 'campaigns', icon: SettingsIcon, label: 'Кампании', color: 'purple' },
            { id: 'scheduled', icon: Clock, label: 'Запланированные', color: 'orange' },
            { id: 'templates', icon: FileText, label: 'Шаблоны', color: 'blue' },
            { id: 'stats', icon: TrendingUp, label: 'Статистика', color: 'green' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-200 ${getTabClass(tab.id, tab.color)}`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] custom-scrollbar">
          {/* Вкладка Кампании */}
          {activeTab === 'campaigns' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-white">Активные кампании</h3>
                  <p className="text-sm text-gray-400 mt-1">Настройте цепочки сообщений для автоматической отправки в Telegram</p>
                </div>
                <button
                  onClick={createCampaign}
                  disabled={!systemSettings.enabled}
                  className={`px-5 py-2.5 rounded-xl flex items-center gap-2 shadow-lg transition-all duration-200 ${
                    systemSettings.enabled
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Создать кампанию
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <Loader className="w-10 h-10 text-purple-500 animate-spin mx-auto mb-3" />
                  <p className="text-gray-400">Загрузка кампаний...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.length === 0 ? (
                    <div className="text-center py-16 bg-gray-800/30 rounded-2xl border border-gray-700/50 backdrop-blur-sm">
                      <Database className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 text-lg">Нет созданных кампаний</p>
                      <button
                        onClick={createCampaign}
                        disabled={!systemSettings.enabled}
                        className={`mt-4 px-6 py-2.5 rounded-xl transition-all ${
                          systemSettings.enabled
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Создать первую кампанию
                      </button>
                    </div>
                  ) : (
                    campaigns.map(campaign => (
                      <div
                        key={campaign.id}
                        className={`bg-gray-800/50 rounded-xl border transition-all duration-200 overflow-hidden ${
                          selectedCampaign?.id === campaign.id && showEditor
                            ? 'border-purple-500 shadow-lg shadow-purple-500/20'
                            : 'border-gray-700/50 hover:border-gray-600'
                        }`}
                      >
                        <div className="p-5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`relative ${campaign.isActive ? 'animate-pulse' : ''}`}>
                                <div className={`w-3 h-3 rounded-full ${campaign.isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
                              </div>
                              <h4 className="text-white font-semibold text-lg">{campaign.name}</h4>
                              {campaign.isActive ? (
                                <span className="text-xs px-2.5 py-1 bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                                  Активна
                                </span>
                              ) : (
                                <span className="text-xs px-2.5 py-1 bg-gray-500/20 text-gray-400 rounded-full border border-gray-500/30">
                                  Отключена
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  setSelectedCampaign(campaign);
                                  setShowEditor(selectedCampaign?.id === campaign.id ? false : true);
                                }}
                                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                                title="Редактировать"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => toggleCampaign(campaign)}
                                className={`p-2 rounded-lg transition-colors ${
                                  campaign.isActive 
                                    ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10' 
                                    : 'text-green-400 hover:text-green-300 hover:bg-green-400/10'
                                }`}
                                title={campaign.isActive ? "Отключить" : "Включить"}
                                disabled={!systemSettings.enabled && !campaign.isActive}
                              >
                                {campaign.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => deleteCampaign(campaign.id)}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                title="Удалить"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-400 mt-2">{campaign.description}</p>
                          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
                            <div className="flex items-center gap-1.5 text-gray-500">
                              <MessageSquare className="w-3.5 h-3.5" />
                              <span>{campaign.messageSequence?.filter(m => m.enabled !== false).length || 0} сообщений</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-500">
                              <Image className="w-3.5 h-3.5" />
                              <span>{campaign.messageSequence?.filter(m => m.hasImage).length || 0} с изображениями</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-500">
                              <Users className="w-3.5 h-3.5" />
                              <span>Получатели: {
                                campaign.sendTo === 'all' ? 'Все' : 
                                campaign.sendTo === 'premium' ? 'Premium' :
                                campaign.sendTo === 'free' ? 'Free' : 'Выборочно'
                              }</span>
                            </div>
                          </div>
                        </div>

                        {selectedCampaign?.id === campaign.id && showEditor && (
                          <div className="p-5 border-t border-gray-700/50 bg-gray-900/30">
                            <CampaignEditor
                              campaign={campaign}
                              templates={templates}
                              onUpdate={updateCampaign}
                              onClose={() => setShowEditor(false)}
                              showNotification={showNotification}
                              systemEnabled={systemSettings.enabled}
                            />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Вкладка Запланированные сообщения с множественным выбором */}
          {activeTab === 'scheduled' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-white">Запланированные сообщения</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {bulkDeleteMode 
                      ? `Выбрано пользователей: ${selectedUserIds.size} из ${getUniqueUserCount()}`
                      : `Все сообщения, запланированные к отправке (${scheduledMessages.length})`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {bulkDeleteMode ? (
                    <>
                      <button
                        onClick={selectAllUsers}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                      >
                        <CheckSquare className="w-4 h-4" />
                        Выбрать всех
                      </button>
                      <button
                        onClick={clearSelection}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <Square className="w-4 h-4" />
                        Снять выделение
                      </button>
                      <button
                        onClick={bulkRemoveUsers}
                        disabled={selectedUserIds.size === 0}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                          selectedUserIds.size > 0
                            ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <Trash className="w-4 h-4" />
                        Удалить выбранных ({selectedUserIds.size})
                      </button>
                      <button
                        onClick={() => {
                          setBulkDeleteMode(false);
                          setSelectedUserIds(new Set());
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Отмена
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setBulkDeleteMode(true)}
                      className="flex items-center gap-2 px-3 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
                    >
                      <UserX className="w-4 h-4" />
                      Массовое удаление
                    </button>
                  )}
                  <button
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      autoRefresh 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                    Автообновление
                  </button>
                  <button
                    onClick={() => loadScheduledMessages()}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Обновить
                  </button>
                </div>
              </div>

              {scheduledLoading ? (
                <div className="text-center py-12">
                  <Loader className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
                  <p className="text-gray-400">Загрузка запланированных сообщений...</p>
                </div>
              ) : scheduledMessages.length === 0 ? (
                <div className="text-center py-16 bg-gray-800/30 rounded-2xl border border-gray-700/50">
                  <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">Нет запланированных сообщений</p>
                  <p className="text-sm text-gray-500 mt-1">Все сообщения отправлены или очередь пуста</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledMessages.map((item, idx) => (
                    <div key={`${item.broadcastId}_${item.step}`} className={`bg-gray-800/50 rounded-xl border p-4 transition-all ${
                      bulkDeleteMode && selectedUserIds.has(item.userId)
                        ? 'border-red-500 bg-red-900/20 shadow-lg shadow-red-500/20'
                        : 'border-gray-700/50 hover:border-gray-600'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          {bulkDeleteMode && (
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(item.userId)}
                              onChange={() => toggleSelectUser(item.userId)}
                              className="mt-2 w-4 h-4 rounded bg-gray-700 border-gray-600 cursor-pointer"
                            />
                          )}
                          {item.hasImage ? (
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                              <ImageIcon className="w-5 h-5 text-purple-400" />
                            </div>
                          ) : (
                            <div className="p-2 bg-blue-500/20 rounded-lg">
                              <MessageSquare className="w-5 h-5 text-blue-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-white font-medium">{item.userName || `User ${item.userId}`}</span>
                              <span className="text-xs text-gray-500">ID: {item.userId}</span>
                              <span className="text-xs px-2 py-0.5 bg-gray-700 rounded-full text-gray-300">
                                Шаг {item.step + 1}
                              </span>
                            </div>
                            <p className="text-sm text-gray-300 mt-1 line-clamp-2">{item.subject}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Timer className="w-3 h-3" />
                                <span className={new Date(item.scheduledAt) <= new Date() ? 'text-green-400' : ''}>
                                  {formatTimeRemaining(item.scheduledAt)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>{formatDate(item.scheduledAt)}</span>
                              </div>
                              {item.hasImage && (
                                <div className="flex items-center gap-1 text-purple-400">
                                  <Image className="w-3 h-3" />
                                  <span>с изображением</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            ⏳ Запланировано
                          </span>
                          {!bulkDeleteMode && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => removeUserFromQueue(item.userId, item.userName || `User ${item.userId}`)}
                                disabled={removingItem === item.userId}
                                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Удалить все сообщения пользователя"
                              >
                                {removingItem === item.userId ? (
                                  <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserX className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => removeMessageFromQueue(item.broadcastId, item.step, item.userId, item.userName || `User ${item.userId}`)}
                                disabled={removingItem === `${item.broadcastId}_${item.step}`}
                                className="p-1.5 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded-lg transition-colors"
                                title="Удалить только это сообщение"
                              >
                                {removingItem === `${item.broadcastId}_${item.step}` ? (
                                  <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Вкладка Шаблоны */}
          {activeTab === 'templates' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-white">Шаблоны сообщений</h3>
                  <p className="text-sm text-gray-400 mt-1">Создавайте и редактируйте шаблоны для рассылок в Telegram</p>
                </div>
                <button
                  onClick={createTemplate}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 flex items-center gap-2 shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                  Создать шаблон
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {templates.map(template => (
                  <div key={template.id} className="group bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-gray-600 transition-all duration-200 overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{template.icon || '📝'}</span>
                          <div>
                            <h4 className="text-white font-semibold">{template.name}</h4>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{template.subject}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingTemplate(template);
                              setShowTemplateEditor(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                            title="Редактировать"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTemplate(template.id)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                            title="Удалить"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-700/50">
                        <div className="flex flex-wrap gap-1.5">
                          {template.variables?.slice(0, 3).map((varName, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full font-mono">
                              {'{{' + varName + '}}'}
                            </span>
                          ))}
                          {template.variables?.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">
                              +{template.variables.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Вкладка Статистика */}
          {activeTab === 'stats' && stats && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">Статистика рассылок</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                <div className="bg-gradient-to-br from-purple-600/20 to-purple-700/20 rounded-xl p-5 border border-purple-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <Gift className="w-5 h-5 text-purple-400" />
                    <span className="text-xs text-purple-300">Всего</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats.total || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">рассылок</p>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-xl p-5 border border-yellow-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    <span className="text-xs text-yellow-300">В процессе</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats.pending || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">активных рассылок</p>
                </div>
                
                <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-5 border border-green-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-xs text-green-300">Завершено</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats.sent || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">успешных рассылок</p>
                </div>
                
                <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-xl p-5 border border-blue-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <MessageSquare className="w-5 h-5 text-blue-400" />
                    <span className="text-xs text-blue-300">Сообщения</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{stats.sentMessages || 0}<span className="text-lg">/{stats.totalMessages || 0}</span></p>
                  <p className="text-xs text-gray-400 mt-1">отправлено сообщений</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-gray-800/30 rounded-xl p-6 border border-gray-700/50">
                  <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart className="w-4 h-4 text-purple-400" />
                    Информация о системе
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                      <span className="text-gray-400">Статус системы</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        systemSettings.enabled 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {systemSettings.enabled ? 'Активна' : 'Отключена'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                      <span className="text-gray-400">Всего кампаний</span>
                      <span className="text-white font-medium">{campaigns.length}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                      <span className="text-gray-400">Активных кампаний</span>
                      <span className="text-white font-medium">{campaigns.filter(c => c.isActive).length}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-400">Шаблонов в базе</span>
                      <span className="text-white font-medium">{templates.length}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-800/30 rounded-xl p-6 border border-gray-700/50">
                  <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                    <Image className="w-4 h-4 text-purple-400" />
                    Медиа-статистика
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                      <span className="text-gray-400">Сообщений с изображениями</span>
                      <span className="text-white font-medium">
                        {campaigns.reduce((sum, c) => sum + (c.messageSequence?.filter(m => m.hasImage).length || 0), 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-400">Всего запланировано сообщений</span>
                      <span className="text-white font-medium">{stats.pending || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно редактора шаблона */}
      {showTemplateEditor && editingTemplate && (
        <TemplateEditorModal
          template={editingTemplate}
          onSave={updateTemplate}
          onClose={() => {
            setEditingTemplate(null);
            setShowTemplateEditor(false);
          }}
        />
      )}
    </div>
  );
};



// Компонент редактора кампании (исправленная версия)
const CampaignEditor = ({ campaign, templates, onUpdate, onClose, showNotification, systemEnabled }) => {
  const [editedCampaign, setEditedCampaign] = useState({ ...campaign });
  const [expandedMessages, setExpandedMessages] = useState({});
  const [uploadingImage, setUploadingImage] = useState({});
  const [deletingImage, setDeletingImage] = useState({});
  const [imagePreview, setImagePreview] = useState({});

// Функция для нормализации данных сообщения
const normalizeMessage = (msg) => {
  // Проверяем наличие imageUrl - если есть, то hasImage должен быть true
  const hasImageFromUrl = !!(msg.imageUrl && msg.imageUrl !== null && msg.imageUrl !== '');
  
  return {
    ...msg,
    // hasImage = существующий флаг ИЛИ наличие imageUrl
    hasImage: msg.hasImage === true || hasImageFromUrl,
    // Если есть imageUrl, но hasImage не установлен - исправляем
    ...(hasImageFromUrl && !msg.hasImage ? { hasImage: true } : {})
  };
};

  // Инициализация превью изображений при загрузке кампании
  useEffect(() => {
    console.log('📋 CampaignEditor mounted with campaign:', campaign);
    console.log('📋 Campaign name:', campaign?.name);
    console.log('📋 Original message sequence:', campaign?.messageSequence);
    
    // Нормализуем данные сообщений
    const normalizedMessages = campaign.messageSequence?.map(normalizeMessage) || [];
    console.log('📋 Normalized message sequence:', normalizedMessages);
    
    const previews = {};
    if (normalizedMessages) {
      normalizedMessages.forEach((msg, idx) => {
        console.log(`📋 Message ${idx}:`, {
          hasImage: msg.hasImage,
          imageUrl: msg.imageUrl,
          type: msg.type
        });
        
        if (msg.hasImage && msg.imageUrl) {
          // Формируем полный URL для превью
          const fullUrl = msg.imageUrl.startsWith('http') 
            ? msg.imageUrl 
            : `${API_URL}${msg.imageUrl}`;
          previews[idx] = fullUrl;
          console.log(`✅ Message ${idx} image preview URL:`, fullUrl);
        } else {
          console.log(`❌ Message ${idx} has no image`);
        }
      });
    }
    
    // Обновляем состояние с нормализованными данными
    setEditedCampaign(prev => ({
      ...prev,
      messageSequence: normalizedMessages
    }));
    setImagePreview(previews);
    console.log('📋 Initialized imagePreview state:', previews);
  }, [campaign]);

  const handleSave = () => {
    console.log('💾 Saving campaign:', editedCampaign);
    onUpdate(editedCampaign);
  };

  const handleMessageChange = (index, field, value) => {
    const updatedMessages = [...(editedCampaign.messageSequence || [])];
    updatedMessages[index] = { ...updatedMessages[index], [field]: value };
    
    // Если поле imageUrl меняется, обновляем hasImage соответственно
    if (field === 'imageUrl') {
      updatedMessages[index].hasImage = !!value;
    }
    
    setEditedCampaign({ ...editedCampaign, messageSequence: updatedMessages });
    console.log(`📝 Changed message ${index} field ${field} to:`, value);
  };

  const uploadImageForMessage = async (index, file) => {
    if (!file) return;
    
    console.log(`📤 Uploading image for message ${index}:`, file.name, file.size);
    setUploadingImage(prev => ({ ...prev, [index]: true }));
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', 'broadcast_images');
    
    try {
      const response = await axios.post(`${API_URL}/api/gift-broadcast/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      console.log('📥 Upload response:', response.data);
      
      if (response.data.success) {
        // Сохраняем относительный URL в БД
        const relativeUrl = response.data.url;
        // Полный URL для превью
        const fullImageUrl = relativeUrl.startsWith('http') 
          ? relativeUrl 
          : `${API_URL}${relativeUrl}`;
        
        console.log(`✅ Image uploaded successfully. Relative URL: ${relativeUrl}, Full URL: ${fullImageUrl}`);
        
        // Обновляем и hasImage, и imageUrl
        handleMessageChange(index, 'imageUrl', relativeUrl);
        
        setImagePreview(prev => ({ ...prev, [index]: fullImageUrl }));
        
        console.log(`📋 Updated message ${index} with imageUrl: ${relativeUrl} and hasImage: true`);
        console.log(`📋 imagePreview state for message ${index}: ${fullImageUrl}`);
        
        showNotification('success', 'Изображение загружено');
      } else {
        console.error('❌ Upload failed:', response.data.error);
        showNotification('error', response.data.error || 'Ошибка загрузки изображения');
      }
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      console.error('Error details:', error.response?.data);
      showNotification('error', error.response?.data?.error || 'Ошибка загрузки изображения');
    } finally {
      setUploadingImage(prev => ({ ...prev, [index]: false }));
    }
  };

  const deleteImageFromServer = async (imageUrl) => {
    if (!imageUrl) return true;
    
    console.log(`🗑️ Deleting image from server: ${imageUrl}`);
    
    try {
      // Извлекаем filename из URL
      const filename = imageUrl.split('/').pop();
      
      const response = await axios.delete(`${API_URL}/api/gift-broadcast/images/${filename}`, {
        data: { filename }
      });
      
      console.log('🗑️ Delete response:', response.data);
      return response.data.success;
    } catch (error) {
      console.error('❌ Error deleting image from server:', error);
      return true;
    }
  };

  const removeImage = async (index) => {
    const message = editedCampaign.messageSequence?.[index];
    if (!message) return;
    
    console.log(`🗑️ Removing image from message ${index}, imageUrl: ${message.imageUrl}`);
    setDeletingImage(prev => ({ ...prev, [index]: true }));
    
    try {
      // Пытаемся удалить изображение с сервера
      await deleteImageFromServer(message.imageUrl);
      
      // Удаляем из кампании
      handleMessageChange(index, 'imageUrl', null);
      
      setImagePreview(prev => {
        const newPreview = { ...prev };
        delete newPreview[index];
        console.log('📋 Updated imagePreview after removal:', newPreview);
        return newPreview;
      });
      showNotification('success', 'Изображение удалено');
    } catch (error) {
      console.error('❌ Error removing image:', error);
      // Все равно удаляем из UI
      handleMessageChange(index, 'imageUrl', null);
      setImagePreview(prev => {
        const newPreview = { ...prev };
        delete newPreview[index];
        return newPreview;
      });
      showNotification('warning', 'Изображение удалено из кампании, но файл остался на сервере');
    } finally {
      setDeletingImage(prev => ({ ...prev, [index]: false }));
    }
  };

  const getFullImageUrl = (index) => {
    const message = editedCampaign.messageSequence?.[index];
    console.log(`🔍 Getting full image URL for message ${index}:`, {
      hasImage: message?.hasImage,
      imageUrl: message?.imageUrl,
      hasPreview: !!imagePreview[index],
      preview: imagePreview[index]
    });
    
    if (!message) return null;
    
    // Проверяем hasImage или наличие imageUrl
    const hasImageFlag = message.hasImage === true || (message.imageUrl && message.imageUrl !== null);
    
    if (!hasImageFlag) {
      console.log(`❌ Message ${index} has no image flag`);
      return null;
    }
    
    // Сначала проверяем preview, потом imageUrl из сообщения
    if (imagePreview[index]) {
      console.log(`✅ Using preview for message ${index}: ${imagePreview[index]}`);
      return imagePreview[index];
    }
    
    if (message.imageUrl) {
      const fullUrl = message.imageUrl.startsWith('http') 
        ? message.imageUrl 
        : `${API_URL}${message.imageUrl}`;
      console.log(`✅ Generated full URL for message ${index}: ${fullUrl} from ${message.imageUrl}`);
      return fullUrl;
    }
    
    console.log(`❌ No image URL for message ${index}`);
    return null;
  };

  const addMessage = () => {
    const newMessage = {
      type: `custom_${Date.now()}`,
      day: (editedCampaign.messageSequence?.length || 0) + 1,
      delay: ((editedCampaign.messageSequence?.length || 0) + 1) * 24,
      subject: 'Новое сообщение',
      enabled: true,
      hasImage: false,
      imageUrl: null,
      imageId: null
    };
    setEditedCampaign({
      ...editedCampaign,
      messageSequence: [...(editedCampaign.messageSequence || []), newMessage]
    });
    showNotification('info', 'Новое сообщение добавлено в цепочку');
  };

  const removeMessage = (index) => {
    if (window.confirm('Удалить это сообщение из цепочки?')) {
      const message = editedCampaign.messageSequence?.[index];
      if (message?.imageUrl) {
        deleteImageFromServer(message.imageUrl);
      }
      
      setEditedCampaign({
        ...editedCampaign,
        messageSequence: editedCampaign.messageSequence.filter((_, i) => i !== index)
      });
      
      setImagePreview(prev => {
        const newPreview = { ...prev };
        delete newPreview[index];
        return newPreview;
      });
      
      showNotification('success', 'Сообщение удалено');
    }
  };

  const toggleMessageExpand = (index) => {
    setExpandedMessages(prev => ({ ...prev, [index]: !prev[index] }));
  };

  console.log('🎨 Rendering CampaignEditor, current imagePreview:', imagePreview);
  console.log('🎨 Current editedCampaign messageSequence:', editedCampaign.messageSequence);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Название кампании</label>
          <input
            type="text"
            value={editedCampaign.name}
            onChange={(e) => setEditedCampaign({ ...editedCampaign, name: e.target.value })}
            className="w-full px-4 py-2.5 bg-gray-900/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            placeholder="Введите название кампании"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Описание</label>
          <input
            type="text"
            value={editedCampaign.description}
            onChange={(e) => setEditedCampaign({ ...editedCampaign, description: e.target.value })}
            className="w-full px-4 py-2.5 bg-gray-900/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            placeholder="Краткое описание кампании"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Кому отправлять</label>
        <select
          value={editedCampaign.sendTo}
          onChange={(e) => setEditedCampaign({ ...editedCampaign, sendTo: e.target.value })}
          className="w-full px-4 py-2.5 bg-gray-900/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
        >
          <option value="all">👥 Все пользователи</option>
          <option value="premium">👑 Только Premium</option>
          <option value="free">📱 Только Free</option>
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-300">Цепочка сообщений</label>
          <button
            onClick={addMessage}
            disabled={!systemEnabled}
            className={`text-sm flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
              systemEnabled
                ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/10'
                : 'text-gray-500 cursor-not-allowed'
            }`}
          >
            <Plus className="w-4 h-4" />
            Добавить сообщение
          </button>
        </div>
        <div className="space-y-3">
          {editedCampaign.messageSequence?.map((message, idx) => {
            const imageUrlToShow = getFullImageUrl(idx);
            console.log(`🖼️ Message ${idx} render - hasImage: ${message.hasImage}, imageUrl: ${message.imageUrl}, imageUrlToShow: ${imageUrlToShow}`);
            
            return (
              <div key={idx} className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
                <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/30 transition-colors"
                     onClick={() => toggleMessageExpand(idx)}>
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={message.enabled !== false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleMessageChange(idx, 'enabled', e.target.checked);
                      }}
                      className="rounded bg-gray-700 border-gray-600 w-4 h-4"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex items-center gap-2">
                      {(message.hasImage || message.imageUrl) ? (
                        <Image className="w-4 h-4 text-purple-400" />
                      ) : (
                        <MessageSquare className="w-4 h-4 text-purple-400" />
                      )}
                      <span className="text-white font-medium">Сообщение {idx + 1}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      через {message.delay} ч.
                    </div>
                    <div className="text-xs text-gray-500">
                      {templates.find(t => t.type === message.type)?.name || message.type || 'Не выбран'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedMessages[idx] ? 'rotate-180' : ''}`} />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMessage(idx);
                      }}
                      className="text-red-400 hover:text-red-300 p-1 rounded-lg hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {expandedMessages[idx] && (
                  <div className="p-4 border-t border-gray-700 bg-gray-800/30 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Задержка (часы)</label>
                        <input
                          type="number"
                          value={message.delay}
                          onChange={(e) => handleMessageChange(idx, 'delay', parseInt(e.target.value))}
                          className="w-full px-3 py-2 bg-gray-800 text-white text-sm border border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Шаблон</label>
                        <select
                          value={message.type}
                          onChange={(e) => handleMessageChange(idx, 'type', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 text-white text-sm border border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          {templates.filter(t => t.isActive !== false).map(t => (
                            <option key={t.id} value={t.type}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Тема сообщения</label>
                      <input
                        type="text"
                        placeholder="Тема сообщения"
                        value={message.subject || ''}
                        onChange={(e) => handleMessageChange(idx, 'subject', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 text-white text-sm border border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>
                    
                    {/* Блок загрузки/удаления изображения */}
                    <div className="pt-2">
                      <label className="text-xs text-gray-400 block mb-2 flex items-center gap-2">
                        <Image className="w-3 h-3" />
                        Изображение для сообщения
                      </label>
                      
                      {(message.hasImage || message.imageUrl) && imageUrlToShow ? (
                        <div className="relative inline-block group">
                          <img 
                            src={imageUrlToShow} 
                            alt={`Preview for message ${idx + 1}`} 
                            className="max-w-[200px] max-h-[150px] rounded-lg border border-gray-600 object-cover"
                            onLoad={() => console.log(`✅ Image loaded successfully for message ${idx}: ${imageUrlToShow}`)}
                            onError={(e) => { 
                              console.error(`❌ Failed to load image for message ${idx}: ${imageUrlToShow}`);
                              e.target.style.display = 'none';
                              e.target.parentElement?.querySelector('.error-placeholder')?.classList.remove('hidden');
                            }}
                          />
                          <div className="error-placeholder hidden absolute inset-0 bg-gray-700 rounded-lg flex items-center justify-center text-xs text-gray-400">
                            Ошибка загрузки
                          </div>
                          <button
                            onClick={() => removeImage(idx)}
                            disabled={deletingImage[idx]}
                            className={`absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg ${
                              deletingImage[idx] ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title="Удалить изображение"
                          >
                            {deletingImage[idx] ? (
                              <Loader className="w-3 h-3 animate-spin" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <label className={`flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 text-sm rounded-lg cursor-pointer hover:bg-gray-700 transition-colors ${uploadingImage[idx] ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <Upload className="w-4 h-4" />
                            {uploadingImage[idx] ? 'Загрузка...' : 'Загрузить изображение'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={uploadingImage[idx]}
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  uploadImageForMessage(idx, e.target.files[0]);
                                }
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <span className="text-xs text-gray-500">JPG, PNG, GIF (до 5MB)</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          onClick={onClose}
          className="px-5 py-2.5 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors font-medium"
        >
          Отмена
        </button>
        <button
          onClick={handleSave}
          className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-medium flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Сохранить кампанию
        </button>
      </div>
    </div>
  );
};




// Компонент редактора шаблона (остается без изменений)
const TemplateEditorModal = ({ template, onSave, onClose }) => {
  const [editedTemplate, setEditedTemplate] = useState({ ...template });
  const [previewMode, setPreviewMode] = useState(false);
  const [newVariable, setNewVariable] = useState('');

  const handleSave = () => {
    onSave(editedTemplate);
    onClose();
  };

  const addVariable = () => {
    if (newVariable.trim() && !editedTemplate.variables?.includes(newVariable.trim())) {
      setEditedTemplate({
        ...editedTemplate,
        variables: [...(editedTemplate.variables || []), newVariable.trim()]
      });
      setNewVariable('');
    }
  };

  const removeVariable = (varName) => {
    setEditedTemplate({
      ...editedTemplate,
      variables: editedTemplate.variables?.filter(v => v !== varName) || []
    });
  };

  const previewText = () => {
    let text = editedTemplate.text || '';
    (editedTemplate.variables || []).forEach(varName => {
      text = text.replace(new RegExp(`{{${varName}}}`, 'g'), `[${varName}]`);
    });
    return text;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60] backdrop-blur-md">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700/50">
        <div className="px-6 py-5 border-b border-gray-700/50 flex items-center justify-between bg-gray-800/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Редактирование шаблона</h3>
              <p className="text-xs text-gray-400 mt-0.5">Настройте содержимое и переменные сообщения для Telegram</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-xl transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] custom-scrollbar">
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Название шаблона</label>
                <input
                  type="text"
                  value={editedTemplate.name}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-800/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Например: Приветственное сообщение"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Тип (ключ)</label>
                <input
                  type="text"
                  value={editedTemplate.type}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, type: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-800/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="unique_template_key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Иконка (emoji)</label>
                <input
                  type="text"
                  value={editedTemplate.icon}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, icon: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-800/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="🎁"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Тема сообщения</label>
              <input
                type="text"
                value={editedTemplate.subject}
                onChange={(e) => setEditedTemplate({ ...editedTemplate, subject: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-800/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Тема сообщения"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">Содержимое сообщения</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewMode(false)}
                    className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-all ${
                      !previewMode 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    Редактор
                  </button>
                  <button
                    onClick={() => setPreviewMode(true)}
                    className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-all ${
                      previewMode 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <EyeIcon className="w-4 h-4" />
                    Предпросмотр
                  </button>
                </div>
              </div>
              {!previewMode ? (
                <textarea
                  value={editedTemplate.text}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, text: e.target.value })}
                  rows={12}
                  className="w-full px-4 py-3 bg-gray-800/50 text-white border border-gray-700 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите текст сообщения. Используйте {{user.fullName}}, {{gift.title}} и другие переменные"
                />
              ) : (
                <div className="w-full p-6 bg-gray-800 rounded-xl overflow-auto max-h-96">
                  <div className="whitespace-pre-wrap text-gray-200 font-sans">
                    {previewText()}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Переменные</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {(editedTemplate.variables || []).map((varName, idx) => (
                  <span key={idx} className="group inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 text-purple-300 text-sm rounded-full font-mono">
                    {'{{' + varName + '}}'}
                    <button
                      onClick={() => removeVariable(varName)}
                      className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newVariable}
                  onChange={(e) => setNewVariable(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addVariable()}
                  placeholder="Название переменной (например: user.fullName, gift.title)"
                  className="flex-1 px-4 py-2.5 bg-gray-800/50 text-white border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <button
                  onClick={addVariable}
                  className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-medium"
                >
                  Добавить
                </button>
              </div>
              <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <p className="text-xs text-blue-300 flex items-center gap-1">
                  <span className="text-blue-400">💡</span>
                  Доступные переменные:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-xs px-2 py-1 bg-gray-800 rounded font-mono text-gray-300">{"{{user.fullName}}"}</span>
                  <span className="text-xs px-2 py-1 bg-gray-800 rounded font-mono text-gray-300">{"{{user.username}}"}</span>
                  <span className="text-xs px-2 py-1 bg-gray-800 rounded font-mono text-gray-300">{"{{user.firstName}}"}</span>
                  <span className="text-xs px-2 py-1 bg-gray-800 rounded font-mono text-gray-300">{"{{gift.title}}"}</span>
                  <span className="text-xs px-2 py-1 bg-gray-800 rounded font-mono text-gray-300">{"{{gift.count}}"}</span>
                  <span className="text-xs px-2 py-1 bg-gray-800 rounded font-mono text-gray-300">{"{{gift.names}}"}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editedTemplate.isActive !== false}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, isActive: e.target.checked })}
                  className="rounded bg-gray-700 border-gray-600 w-4 h-4"
                />
                <span className="text-sm text-gray-300">Шаблон активен</span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-700/50 flex justify-end gap-3 bg-gray-800/30">
          <button onClick={onClose} className="px-5 py-2.5 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors font-medium">
            Отмена
          </button>
          <button onClick={handleSave} className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all font-medium flex items-center gap-2">
            <Save className="w-4 h-4" />
            Сохранить шаблон
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftBroadcastPanel;